import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";

import BulkListModal from "./BulkListModal";
import UnlistedPartSearchForm from "./UnlistedPartSearchForm";
import UnlistedImageCell from "./UnlistedImageCell";
import { PageMode } from "../../../models/PagedTable";
import PagedTable, { IPagedModeToggle } from "../../shared/Tables/PagedTable";
import UnlistedPriceStockCell from "./UnlistedPriceStockCell";
import UnlistedPartTypeCell from "./UnlistedPartTypeCell";
import UnlistedDonorVehicleCell from "./UnlistedDonorVehicleCell";
import UnlistedListingWorkflowCell from "./UnlistedListingWorkflowCell";
import { IPart } from "../../../models/Part";
import usePartMutations from "../../../data/usePartMutations";
import useNotification from "../../notifications/useNotifications";
import UnlistedExpandedRowContent from "./UnlistedExpandedRowContent";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import ChangeCategoryModal from "../../stock/stockFunctions/ChangeCatgeoryModal";
import useTemplateProductDescription from "../../../data/useTemplateProductDescription";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import { FormatType } from "../../../models/EbayOffer";
import { useQueryClient } from "@tanstack/react-query";
import TableCheckboxCol, { IndeterminateCheckbox } from "../../shared/Tables/IndeterminateCheckbox";
import useCategoriesPartial from "../../../data/useCategoriesPartial";
import openInNewTab from "../../../utils/openInNewTab";
import UnlistedEbayTitleAboveRow from "./UnlistedEbayTitleAboveRow";

export const UNLISTED_LIST_MODE_TOGGLES: IPagedModeToggle[] = [
  { label: "Ready to list", pageMode: PageMode.UnlistedReadyToList },
  { label: "Missing/Deleted", pageMode: PageMode.UnlistedMissingDeleted },
  { label: "QC", pageMode: PageMode.UnlistedQC },
  { label: "Dismantled", pageMode: PageMode.UnlistedDismantled },
  { label: "Located", pageMode: PageMode.UnlistedLocated },
  { label: "No Images", pageMode: PageMode.UnlistedNoImages },
  { label: "No Price", pageMode: PageMode.UnlistedNoPrice },
  { label: "No MPN", pageMode: PageMode.UnlistedNoMpn },
  { label: "Has Price", pageMode: PageMode.UnlistedHasPrice },
  { label: "Has MPN", pageMode: PageMode.UnlistedHasMpn },
  { label: "Has OE MPN", pageMode: PageMode.UnlistedHasOeMpn },
];

type EbayUnlistedTableProps = {
  /** Optional controlled toggle state — when provided, the parent owns the toggle array. */
  listModeToggles?: boolean[];
  onListModeToggle?: (index: number) => void;
};

export default function EbayUnlistedTable({
  listModeToggles: listModeTogglesProp,
  onListModeToggle: onListModeToggleProp,
}: EbayUnlistedTableProps = {}) {
  const { autoReorderImages } = usePartMutations();
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { bulkUpdateProductDescription } = useTemplateProductDescription();
  const { categories: allCategories } = useCategoriesPartial();
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();

  const [bulkListLoading, setBulkListLoading] = useState<boolean>(false);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState<boolean>(false);
  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);
  const tableRef = useRef<Table<IPart> | null>(null);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [appliedQuery, setAppliedQuery] = useState("");
  const [internalListModeToggles, setInternalListModeToggles] = useState<
    boolean[]
  >(() => new Array(UNLISTED_LIST_MODE_TOGGLES.length).fill(false) as boolean[]);
  const listModeToggles = listModeTogglesProp ?? internalListModeToggles;

  const onListModeToggle = (i: number) => {
    if (onListModeToggleProp) {
      onListModeToggleProp(i);
      return;
    }
    setInternalListModeToggles((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  const handleListOnePart = useCallback((p: IPart) => {
    setSelectedPartsForBulkList([p]);
    setBulkListModalOpen(true);
  }, []);

  // const ebayListingOptions = Object.keys(ListingStatusEnum)
  //   .filter(key => isNaN(Number(key)))
  //   .map((key, index) => ({
  //     id: index,
  //     name: key,
  //   }));

  const colHelper = createColumnHelper<IPart>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true, true, "ebay-listings", true),
      colHelper.display({
        id: "images",
        meta: {
          thClassName: "min-w-[12rem] w-52 !p-1.5",
          tdClassName: "min-w-[12rem] w-52 !p-1.5 align-top h-px",
        },
        cell: (info) => <UnlistedImageCell part={info.row.original} />,
      }),
      colHelper.accessor("categoryId", {
        header: "Part information",
        id: "categoryId",
        sortDescFirst: false,
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[11rem] max-w-[14rem] w-[12rem] align-top",
          tdClassName: "min-w-[11rem] max-w-[14rem] w-[12rem] align-top h-px",
        },
        cell: (info) => (
          <UnlistedPartTypeCell
            part={info.row.original}
            categories={allCategories.data?.data}
          />
        ),
      }),
      colHelper.accessor("vehicle.vrm", {
        header: "Donor vehicle",
        id: "vrm",
        sortingFn: "auto",
        meta: {
          thClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top",
          tdClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top h-px",
        },
        cell: (info) => <UnlistedDonorVehicleCell part={info.row.original} />,
      }),
      colHelper.accessor(row => {
        const offer = row.offers?.[0];
        const offerPrice = offer
          ? (offer.format === FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)
          : null;
        return offerPrice ?? row.enteredPrice ?? 0;
      }, {
        id: "offer.price",
        header: "Price & stock",
        meta: {
          thClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top",
          tdClassName: "min-w-[10rem] max-w-[13rem] w-[11rem] align-top h-px",
        },
        cell: (info) => <UnlistedPriceStockCell part={info.row.original} />,
      }),
      colHelper.display({
        id: "listing-workflow",
        header: "Listing workflow",
        enableSorting: false,
        meta: {
          thClassName: "w-[22rem] min-w-[20rem] max-w-[28rem] align-top",
          tdClassName: "w-[22rem] min-w-[20rem] max-w-[28rem] align-top h-px",
        },
        cell: (info) => (
          <div className="flex h-full" onClick={(e) => e.stopPropagation()}>
            <UnlistedListingWorkflowCell
              part={info.row.original}
              onListNow={handleListOnePart}
            />
          </div>
        ),
      }),
      // colHelper.accessor("vehicle", {
      //   id: "mileage",
      //   header: "Mileage",
      //   cell: (info) => <span className="flex justify-center">{info.getValue()?.odometerInMiles?.toLocaleString() ?? "N/A"}</span>,
      // }),
      // colHelper.accessor("created", {
      //   id: "created",
      //   header: "Created",
      //   cell: (info) => <span className="flex justify-center">{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</span>,
      // }),
      // colHelper.accessor("offers", {
      //   id: "offer status",
      //   header: "Offer Status",
      //   enableSorting: false,
      //   cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original} />,
      // }),
    ],
    [allCategories.isLoading, allCategories.data, handleListOnePart]
  );



  const bulkList = (rows: RowModel<IPart>, table: Table<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    tableRef.current = table;
    setSelectedPartsForBulkList(rows.rows.map((row) => row.original));
    setBulkListModalOpen(true);
  };

  const handleBulkListComplete = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
      tableRef.current = null;
    }
  };

  const handleAutoReorderImages = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const ids = selected.map((row) => row.original.id);

    autoReorderImages.mutate(ids, bulkSaveHandlers);
  }

  const handleApplyBulkDefaults = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.id)
    };

    useBulkDefaultPartCategory.mutate(body as any, {
      ...bulkSaveHandlers,
      onSuccess: () => {
        bulkSaveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable", PageMode.Unlisted]);
      }
    });
  };


  const handleBulkResetDescription = (rows: RowModel<IPart>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected());

    if (selected.length === 0) {
      addNotification({
        primaryText: "No parts selected",
        variant: "warn"
      });
      return;
    }

    const partIds = selected.map((row) => row.original.id);

    bulkUpdateProductDescription.mutate({ partIds }, bulkSaveHandlers);
  };


  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const expandedRow = (part: IPart) => {
    return <UnlistedExpandedRowContent part={part} />;
  };

  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache
        .getAll()
        .filter((query) => query.queryKey[0] === "pagedTable");

      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const pagedTableReady = useMemo(
    () => Boolean(queryState && queryState.status !== "loading"),
    [queryState]
  );

  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
          onComplete={handleBulkListComplete}
        />
      )}

      <UnlistedPartSearchForm
        pagedTableReady={pagedTableReady}
        onApplySearch={(searchBody, freeTextQuery) => {
          setAdditionalBody(searchBody);
          setAppliedQuery(freeTextQuery);
        }}
        onClear={() => {
          setAdditionalBody({});
          setAppliedQuery("");
        }}
        listViewFilterOptions={UNLISTED_LIST_MODE_TOGGLES}
        listViewToggles={listModeToggles}
        onListViewToggle={onListModeToggle}
      />

      <div className="mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50/90 px-3 py-2">
          <h3 className="text-sm font-semibold text-slate-800">
            Unlisted parts
          </h3>
          <p className="text-xs text-slate-500">
            Search, refine with the toggles, then use bulk actions to list on
            eBay.
          </p>
        </div>
        <div className="px-3 pt-0 pb-3 [&>div>div:first-child]:mt-0">
          <PagedTable
            url="/part/listings"
            pageMode={PageMode.Unlisted}
            columns={cols}
            queryLabel={undefined}
            externalQuery={appliedQuery}
            customUrlQuery=""
            actionButtons={[
              { label: "Bulk List", onClick: bulkList, isLoading: bulkListLoading, disabledOnNoRows: true },
              { label: "Auto Reorder Images", onClick: handleAutoReorderImages, isLoading: autoReorderImages.isLoading, disabledOnNoRows: true },
              { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
              { label: "Reset Description", onClick: handleBulkResetDescription, isLoading: bulkUpdateProductDescription.isLoading, disabledOnNoRows: true },
              { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
            ]}
            expandedRow={expandedRow}
            highlightExpandedRowGroup
            aboveRowContent={(part, { isExpanded }) => (
              <UnlistedEbayTitleAboveRow part={part} isExpanded={isExpanded} />
            )}
            showTotal
            additionalBody={additionalBody}
            paginationAtBottom
            showFloatingPaginationTop
            slateTableChrome
            stripedRows
            modeToggles={UNLISTED_LIST_MODE_TOGGLES}
            modeTogglesControlled={listModeToggles}
            onModeToggle={onListModeToggle}
            renderModeTogglesInTable={false}
            onRowDoubleClick={(part) => openInNewTab(`stock/${part.id}`)}
          />
        </div>
      </div>
    </>
  );
}
